import { observable, action, computed, configure, runInAction } from "mobx";
import { createContext } from "react";
import { IFttcHour } from "../models/fttchour";
import { ITechnician } from "../models/technician";
import { toast } from "react-toastify";
import agent from "../api/agent";

configure({ enforceActions: "always" });

class FttcHoursStore {
  @observable fttchourRegistry = new Map();
  @observable fttchour: IFttcHour | null = null;
  @observable fttchourList: IFttcHour[] = new Array();
  @observable fttchourTech: IFttcHour[] = new Array();
  @observable payrollcycleRegistry = new Map();
  @observable technicianRegistry = new Map();
  @observable technician: ITechnician | null = null;
  @observable loadingInitial = false;
  @observable submitting = false;
  @observable target = "";

  @computed get fttchournamesByDate() {
    return Array.from(this.fttchourRegistry.values()).sort(
      (a, b) => Date.parse(a.startDate) - Date.parse(b.startDate)
    );
  }

  @computed get techniciansByName() {
    return Array.from(this.technicianRegistry.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }

  @computed get payrollcyclesByName() {
    return Array.from(this.payrollcycleRegistry.values()).sort(
      (a, b) =>
        a.startYear.localeCompare(b.startYear) ||
        a.endYear.localeCompare(b.endYear) ||
        a.payRun.localeCompare(b.payRun)
    );
  }

  @action loadFttcHourTechs = async () => {
    this.loadingInitial = true;
    try {
      const fttchours = await agent.FttcHours.list();
      runInAction("loading fttchours", () => {
        this.fttchourTech = fttchours;
        this.loadingInitial = false;
      });
    } catch (error) {
      runInAction("load FttcHour List error", () => {
        this.loadingInitial = false;
      });
      toast.error("load FttcHour List error");
    }
  };

  @action searchFttcHours = async (search: {
    tech: string;
    payRun: string;
    jobStartDate: string;
    jobEndDate: string;
    orderNo: string;
  }) => {
    if (
      search.tech === "" &&
      search.payRun === "" &&
      search.jobStartDate === "" &&
      search.jobEndDate === "" &&
      search.orderNo === ""
    ) {
      toast.info("Please select at least one search condition");
      return;
    } else if (search.jobStartDate !== "" && search.jobEndDate === "") {
      toast.info("Please select job end date");
      return;
    } else if (search.jobEndDate !== "" && search.jobStartDate === "") {
      toast.info("Please select job start date");
      return;
    }

    const params = new URLSearchParams();
    params.append("tech", search.tech);
    params.append("payRun", search.payRun);
    params.append("jobStartDate", search.jobStartDate);
    params.append("jobEndDate", search.jobEndDate);
    params.append("orderNo", search.orderNo);

    this.loadingInitial = true;
    try {
      const fttchours = await agent.FttcHours.search(params);
      this.fttchourRegistry.clear();
      runInAction("loading fttchours", () => {
        fttchours.forEach((fttchour) => {
          if (fttchour.startDate !== null) {
            fttchour.startDate = fttchour.startDate.substr(0, 10);
          }
          this.fttchourRegistry.set(fttchour.id, fttchour);
        });
        this.loadingInitial = false;
      });
    } catch (error) {
      runInAction("load FttcHour List error", () => {
        this.loadingInitial = false;
      });
      toast.error("load FttcHour List error");
    }
  };

  @action loadFttcHour = async (id: string) => {
    let fttchour = this.getFttcHour(id);
    console.log(fttchour);
    if (fttchour) {
      this.fttchour = fttchour;
      return fttchour;
    } else {
      this.loadingInitial = true;
      try {
        const fttchour = await agent.FttcHours.details(id);
        console.log(fttchour);
        runInAction("getting fttchour", () => {
          if (fttchour.startDate) {
            fttchour.startDate = fttchour.startDate.substr(0, 10);
          }
          this.fttchour = fttchour;
          this.loadingInitial = false;
        });
        return fttchour;
      } catch (error) {
        runInAction("get fttchour error", () => {
          this.loadingInitial = false;
        });
        toast.error("get fttchour error");
        console.log(error);
      }
    }
  };

  @action clearFttcHour = () => {
    this.fttchour = null;
    this.fttchourList.length = 0;
  };

  getFttcHour = (id: string) => {
    return this.fttchourRegistry.get(Number(id));
  };

  @action createFttcHour = async (fttchour: IFttcHour[]) => {
    this.submitting = true;
    try {
      await agent.FttcHours.create(fttchour);
      runInAction("create Technician", () => {
        this.submitting = false;
      });
    } catch (error) {
      runInAction("create FttcHour error", () => {
        this.submitting = false;
      });
      toast.error("create FttcHour error");
      console.log(error);
    }
  };

  @action editFttcHour = async (fttchour: IFttcHour) => {
    this.submitting = true;
    try {
      await agent.FttcHours.update(fttchour);
      runInAction("editing fttchour", () => {
        this.fttchourRegistry.set(fttchour.id, fttchour);
        this.fttchour = fttchour;
        this.submitting = false;
      });
    } catch (error) {
      runInAction("edit fttchour error", () => {
        this.submitting = false;
      });
      toast.error("edit fttchour error");
      console.log(error);
    }
  };

  @action deleteFttcHour = async (id: string) => {
    this.submitting = true;
    this.target = id;
    try {
      await agent.FttcHours.delete(id);
      runInAction("deleting FttcHour", () => {
        this.fttchourRegistry.delete(id);
        this.submitting = false;
        this.target = "";
      });
    } catch (error) {
      runInAction("delete FttcHour error", () => {
        this.submitting = false;
        this.target = "";
      });
      toast.error("delete FttcHour error");
      console.log(error);
    }
  };

  @action importFttcHours = async (data: any) => {
    var rowno = 0;
    try {
      this.fttchourList.length = 0;
      data.forEach((fttchours: any) => {
        rowno = rowno + 1;
        const fttchour: IFttcHour = {
          id: "0",
          createdAt: "",
          updatedAt: "",
          financialYear: fttchours["Financial Year"],
          payRun: fttchours.PayRun,
          workOrder: fttchours["Work Order"],
          sorId: fttchours.SOR,
          description: fttchours["Work Order Lines Description"],
          amount: fttchours.Amount,
          tech: fttchours["Technician"],
          startDate: fttchours["Start Date"],
          comment: fttchours.Comment,
        };
        if (fttchour.startDate == null) {
          fttchour.startDate = "";
        } else {
          if (!isNaN(Number(fttchour.startDate))) {
            let d = new Date(
              (parseInt(fttchour.startDate) - 25569) * 86400 * 1000
            );

            const pad = (s: number) => {
              return s < 10 ? "0" + s : s;
            };
            fttchour.startDate = [
              d.getFullYear(),
              pad(d.getMonth() + 1),
              pad(d.getDate()),
            ].join("-");
          } else {
            let parts = fttchour.startDate.split("/");
            fttchour.startDate =
              parts[2] +
              "-" +
              (parseInt(parts[1]) < 10 ? "0" : "") +
              parseInt(parts[1]) +
              "-" +
              (parseInt(parts[0]) < 10 ? "0" : "") +
              parseInt(parts[0]);
          }
          var regEx = /^\d{4}-\d{2}-\d{2}$/;

          if (
            fttchour.startDate.match(regEx) === null ||
            Date.parse(fttchour.startDate) > Date.now()
          ) {
            throw "Invalid date";
          }
        }

        this.fttchourList.push(fttchour);
      });
      toast.success(
        `${this.fttchourList.length} records successfully imported`
      );
    } catch (error) {
      toast.error(`import excel error, row #${rowno} has data format problem.`);
    }
  };

  @action loadPayrollCycles = async () => {
    this.loadingInitial = true;
    try {
      const payrollcycles = await agent.PayrollCycles.list();
      runInAction("loading payroll cycles", () => {
        payrollcycles.forEach((payrollcycle) => {
          this.payrollcycleRegistry.set(
            payrollcycle.startYear +
              "-" +
              payrollcycle.endYear +
              "-" +
              payrollcycle.payRun,
            payrollcycle
          );
        });
        this.loadingInitial = false;
      });
    } catch (error) {
      runInAction("load payroll cycles error", () => {
        this.loadingInitial = false;
      });
      toast.error("load payroll cycles error");
    }
  };
}

export default createContext(new FttcHoursStore());
